import React from "react";
import moment from "moment";
import DOMPurify from "dompurify";

import { sanitizeURLs } from "../../utils/helpers";
import { Message as MessageInterface } from "../../types/collaborations/chat";

type Props = {
    groupedMessages: Record<string, MessageInterface[]>;
};

// DOMPurify types expect mutable string[] (not readonly tuples)
const allowedTags: string[] = ["b", "i", "u", "strong", "em", "br", "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6", "p", "a"];
const allowedAttr: string[] = ["href", "rel", "target"];

function getDividerLabel(dateIso: string) {
    const m = moment(dateIso);
    if (m.isSame(moment(), "day")) return "Today";
    return `${m.format("ddd")}, ${m.format("DD/MM")}`;
}

function sanitizeMessageHtml(input: string) {
    return DOMPurify.sanitize(sanitizeURLs(input ?? ""), {
        ALLOWED_TAGS: allowedTags,
        ALLOWED_ATTR: allowedAttr,
    });
}

function getMessageGroupKey(msg: MessageInterface) {
    const isHub = !!msg.props.model.isHubMessage;
    const direction = msg.props.model.direction;
    const senderId = msg.props.model.senderId ?? "";
    if (isHub) return `hub:${senderId}`;
    if (direction === "outgoing") return `out:${senderId}`;
    return `in:${senderId}`;
}

export default function RoomMessages({ groupedMessages }: Props) {
    const dateKeys = Object.keys(groupedMessages);

    return (
        <div className="tt-chat-room">
            <div className="tt-chat-room-inner">
                {dateKeys.map((dateKey) => (
                    <React.Fragment key={dateKey}>
                        <div className="tt-chat-room-divider" aria-label={`Messages for ${getDividerLabel(dateKey)}`}>
                            <div className="tt-chat-room-divider-line" />
                            <div className="tt-chat-room-divider-label">{getDividerLabel(dateKey)}</div>
                            <div className="tt-chat-room-divider-line" />
                        </div>

                        {groupedMessages[dateKey].map((msg, i) => {
                            const prev = groupedMessages[dateKey][i - 1];
                            const direction = msg.props.model.direction;
                            const isHubMessage = !!msg.props.model.isHubMessage;
                            const isHubOutgoing = isHubMessage && direction === "outgoing";
                            const isRegularOutgoing = direction === "outgoing" && !isHubMessage;
                            const senderName = msg.props.model.sender ?? "";
                            const senderId = msg.props.model.senderId ?? "";

                            const isPrevIncomingSameSender =
                                !!prev &&
                                prev.props.model.direction === "incoming" &&
                                !prev.props.model.isHubMessage &&
                                direction === "incoming" &&
                                (prev.props.model.senderId ?? "") === senderId;

                            const isPrevHubOutgoingSameSender =
                                !!prev &&
                                !!prev.props.model.isHubMessage &&
                                isHubOutgoing &&
                                (prev.props.model.senderId ?? "") === senderId;

                            const showIncomingSenderLabel =
                                direction === "incoming" && !!senderName && !isPrevIncomingSameSender;

                            const showHubOutgoingSenderLabel =
                                isHubOutgoing && !!senderName && !isPrevHubOutgoingSameSender;

                            const isGroupedWithPrev = isPrevIncomingSameSender || isPrevHubOutgoingSameSender;
                            const isNewGroup = i > 0 && (!prev || getMessageGroupKey(prev) !== getMessageGroupKey(msg));
                            const time = msg.props.model.sentTime;
                            const html = sanitizeMessageHtml(msg.props.model.message ?? "");

                            const rowClass = isHubOutgoing
                                ? "tt-chat-room-bubble-row--hub-outgoing"
                                : isRegularOutgoing
                                  ? "tt-chat-room-bubble-row--outgoing"
                                  : "tt-chat-room-bubble-row--incoming";

                            const bubbleClass = isHubOutgoing
                                ? "tt-chat-room-bubble--hub-outgoing"
                                : isRegularOutgoing
                                  ? "tt-chat-room-bubble--outgoing"
                                  : "tt-chat-room-bubble--incoming";

                            const timeClass = isHubOutgoing || direction === "incoming"
                                ? "tt-chat-room-bubble-time--incoming"
                                : "tt-chat-room-bubble-time--outgoing";

                            return (
                                <React.Fragment key={`${dateKey}-${i}`}>
                                    {showIncomingSenderLabel ? (
                                        <div
                                            className={`tt-chat-room-sender-group-label ${isNewGroup ? "tt-chat-room-sender-group-label--group-start" : ""}`}
                                        >
                                            {senderName}
                                        </div>
                                    ) : null}
                                    {showHubOutgoingSenderLabel ? (
                                        <div
                                            className={`tt-chat-room-sender-group-label tt-chat-room-sender-group-label--hub-outgoing ${isNewGroup ? "tt-chat-room-sender-group-label--group-start" : ""}`}
                                        >
                                            {senderName}
                                        </div>
                                    ) : null}

                                    <div
                                        className={`tt-chat-room-bubble-row ${rowClass} ${isGroupedWithPrev ? "tt-chat-room-bubble-row--grouped" : ""} ${isNewGroup && !showIncomingSenderLabel && !showHubOutgoingSenderLabel ? "tt-chat-room-bubble-row--group-start" : ""}`}
                                    >
                                        <div className={`tt-chat-room-bubble ${bubbleClass}`}>
                                            <div className="tt-chat-room-bubble-text" dangerouslySetInnerHTML={{ __html: html }} />
                                            <div className={`tt-chat-room-bubble-time ${timeClass}`}>
                                                {time}
                                            </div>
                                        </div>
                                    </div>
                                </React.Fragment>
                            );
                        })}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
}

